import { create } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

import { createChannelSlice, type ChannelSlice } from './slices/channelSlice.js'
import { createProgramSlice, type ProgramSlice } from './slices/programSlice.js'
import { createScheduleSlice, type ScheduleSlice } from './slices/scheduleSlice.js'
import { createPlayoutSlice,  type PlayoutSlice  } from './slices/playoutSlice.js'
import { createToastSlice,    type ToastSlice    } from './slices/toastSlice.js'
import { createPrefsSlice,    type PrefsSlice, persistPrefs } from './slices/prefsSlice.js'
import { createAuthSlice,     type AuthSlice } from './slices/authSlice.js'
import { createUndoSlice,     type UndoSlice } from './slices/undoSlice.js'
import { persistNoticeHistory } from '../lib/notifications.js'

// ─── Type du store global ─────────────────────────────────────────────────────

export type RootStore =
  & ChannelSlice
  & ProgramSlice
  & ScheduleSlice
  & PlayoutSlice
  & ToastSlice
  & PrefsSlice
  & AuthSlice
  & UndoSlice

// ─── Store Zustand ────────────────────────────────────────────────────────────

export const useStore = create<RootStore>()(
  devtools(
    subscribeWithSelector(
      immer((...a) => ({
        ...createChannelSlice(...a),
        ...createProgramSlice(...a),
        ...createScheduleSlice(...a),
        ...createPlayoutSlice(...a),
        ...createToastSlice(...a),
        ...createPrefsSlice(...a),
        ...createAuthSlice(...a),
        ...createUndoSlice(...a),
      }))
    ),
    { name: 'TrafficPlay', enabled: import.meta.env.DEV }
  )
)

// ─── Persistance des préférences vers localStorage ────────────────────────────
// On souscrit aux seuls champs de PrefsSlice qui doivent survivre au reload.
useStore.subscribe(
  (s) => ({ zoomAnchor: s.zoomAnchor }),
  (prefs) => persistPrefs(prefs),
  { equalityFn: (a, b) => a.zoomAnchor === b.zoomAnchor },
)

// Historique des notifications : il survit au rechargement de la page.
useStore.subscribe(
  (s) => s.noticeHistory,
  (history) => persistNoticeHistory(history),
)

// ─── Sélecteurs optimisés (évitent les re-renders inutiles) ──────────────────

// Programmes
export const useProgramsUpdateSeq = () => useStore(s => s.programsUpdateSeq)
export const usePrograms          = () => useStore(s => s.programs)
export const useProgramsLoading = () => useStore(s => s.programsLoading)
export const useProgramsTotal   = () => useStore(s => s.programsTotal)
export const useProgramQuery    = () => useStore(s => s.programQuery)
export const useSelectedProgram = () => useStore(s => s.selectedProgram())
export const useProgramDetailState = () => useStore(s => s.programDetail)

// Grilles et items
export const useGrids           = () => useStore(s => s.grids)
export const useActiveGrid      = () => useStore(s => s.activeGrid())
export const useItems           = () => useStore(s => s.items)
export const useItemsLoading    = () => useStore(s => s.itemsLoading)
export const useLiveItems       = () => useStore(s => s.liveItems)
export const useLiveItemsLoading = () => useStore(s => s.liveItemsLoading)
export const useConflicts       = () => useStore(s => s.conflicts)
export const useValidation      = () => useStore(s => s.validationResult)
export const useAgendaView      = () => useStore(s => s.agendaView)
export const useAgendaFocus     = () => useStore(s => s.agendaFocus)
export const useDraggingItem    = () => useStore(s => s.draggingItemId)
export const useOnAirItem       = () => useStore(s => s.onAirItem())
export const useNextItem        = () => useStore(s => s.nextItem())
export const useGridUpdateSeq   = () => useStore(s => s.gridUpdateSeq)
export const useGridsUpdateSeq  = () => useStore(s => s.gridsUpdateSeq)

// Annulation / rétablissement de la dernière action de grille
export const useGridUndoStack = () => useStore(s => s.gridUndoStack)
export const useGridRedoStack = () => useStore(s => s.gridRedoStack)
export const useGridUndoBusy  = () => useStore(s => s.gridUndoBusy)

// Playout temps réel
export const usePlayoutState    = () => useStore(s => s.playoutState)
export const useBridgeConnected = () => useStore(s => s.playoutState?.bridgeConnected ?? false)
export const useWsConnected     = () => useStore(s => s.wsConnected)
export const useWsStateReady    = () => useStore(s => s.wsStateReady && s.wsChannelId === s.activeChannelId)
export const useWsEverReady     = () => useStore(s => s.wsEverReady)
export const useAlerts          = () => useStore(s => s.alerts)
export const useUnreadAlerts    = () => useStore(s => s.unreadAlertCount)
export const useHasActiveAlert  = () => useStore(s => s.hasActiveAlert())
export const useCriticalAlerts  = () => useStore(s => s.criticalAlerts())
export const useLiveSources     = () => useStore(s => s.liveSources)

// Canaux
export const useChannels        = () => useStore(s => s.channels)
export const useActiveChannel   = () => useStore(s => s.activeChannel())
export const useActiveChannelId = () => useStore(s => s.activeChannelId)

// Alerte fin de direct
export const useLiveEndingAlert = () => useStore(s => s.liveEndingAlert)

// Toasts
export const useToasts = () => useStore(s => s.toasts)
export const useNoticeHistory = () => useStore(s => s.noticeHistory)
export const useNoticeCenterOpen = () => useStore(s => s.noticeCenterOpen)
export const useUnreadNoticeCount = () => useStore(s => s.noticeHistory.reduce((n, entry) => n + (entry.read ? 0 : 1), 0))

// Préférences utilisateur (persistées en localStorage)
export const useZoomAnchor = () => useStore(s => s.zoomAnchor)

// Authentification (jamais persistée dans le navigateur)
export const useAuthUser = () => useStore(s => s.authUser)
export const useAuthStatus = () => useStore(s => s.authStatus)
export const useAuthReady = () => useStore(s => s.authReady)
export const useAuthTrustedDeviceDays = () => useStore(s => s.authTrustedDeviceDays)
export const useHasPermission = (permission: import('../types/index.js').Permission) =>
  useStore(s => s.authUser?.role === 'ADMIN' || Boolean(s.authUser?.permissions.includes(permission)))
